import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import {
  controlPlane,
  fetchBands,
  segmentAllows,
  type Band,
  type BandSegment,
  type BandsResponse,
} from './lib/client'
import { rfStore } from './lib/rfstore'
import { acompanharVisibilidade, anunciarSessao, encerrarSessao, soltarTela, travarTela } from './lib/presenca'
import { getAudioDebugLog, type RogerTone } from './lib/audio'
import { Dock, FILTRO_HZ, SQUELCH_DB } from './components/Dock'
import { Waterfall } from './components/Waterfall'
import { MapView } from './components/MapView'
import { Gente } from './components/Gente'
import { Logbook } from './components/Logbook'
import { Awards } from './components/Awards'
import { Shack } from './components/Shack'
import { Station } from './components/Station'
import { Exam } from './components/Exam'
import { Identidade } from './components/Identidade'
import { Admin } from './components/Admin'
import { InstalarApp } from './components/InstalarApp'
import { Login, type User } from './components/Login'
import { LangSelector } from './components/LangSelector'
import type { DialPresence, DialRepeater } from './components/DialStrip'
import { lerPref, gravarPref } from './lib/prefs'
import { useI18n } from './lib/i18n'

/**
 * Casca do app.
 *
 * Três faixas fixas, como manda docs/06 §2.1: barra de estado no topo, conteúdo
 * rolável no meio, e o dock de operação colado no rodapé com as abas abaixo.
 *
 * O que mudou e por quê: era uma rolagem única com exame, logbook, diplomas,
 * loja e estação empilhados junto do rádio, e o PTT a 1400 px do topo. Falar
 * exigia rolar a página inteira. Agora só o RÁDIO ocupa a aba de operar, e o
 * dock não sai do lugar — o polegar nunca viaja.
 */

/**
 * O segmento mais LARGO que passa no filtro — usado por irParaBanda pra achar
 * a área de fonia reconhecida de uma faixa: o segmento mais largo que aceita
 * o modo é, na prática, onde a fonia de verdade se concentra (o resto costuma
 * ser recorte fino de CW/digital em volta).
 */
function segmentoMaisLargo(
  segs: BandSegment[],
  aceita: (s: BandSegment) => boolean,
): BandSegment | undefined {
  return segs.filter(aceita).reduce<BandSegment | undefined>((melhor, s) => {
    const largura = s.high_khz - s.low_khz
    return !melhor || largura > melhor.high_khz - melhor.low_khz ? s : melhor
  }, undefined)
}

type Aba = 'radio' | 'gente' | 'estacao' | 'progresso' | 'admin'

export default function App() {
  const { t } = useI18n()

  // A aba de administração entra na barra só para quem administra. Isto NÃO é
  // controle de acesso — o servidor confere em cada endpoint —, é não oferecer
  // uma porta que não abriria.
  const abas: { id: Aba; nome: string }[] = [
    { id: 'radio', nome: t.app.abaRadio },
    { id: 'gente', nome: t.app.abaGente },
    { id: 'estacao', nome: t.app.abaEstacao },
    { id: 'progresso', nome: t.app.abaProgresso },
  ]
  const abaAdmin: { id: Aba; nome: string } = { id: 'admin', nome: t.app.abaAdmin }

  // undefined = ainda verificando a sessão. Mostrar o login nesse instante
  // faria a tela piscar para quem já está logado.
  const [user, setUser] = useState<User | null | undefined>(undefined)
  const [bandsResp, setBands] = useState<BandsResponse | null>(null)
  const [aba, setAba] = useState<Aba>('radio')
  const [audioErr, setAudioErr] = useState<string | null>(null)
  const [logVersion, setLogVersion] = useState(0)
  // Log de áudio visível na tela — em celular não dá para abrir o devtools,
  // e é assim que um "mudo" em produção vira algo que dá para debugar a
  // distância, com print em vez de acesso ao aparelho. Escondido do uso
  // normal: só aparece com ?logaudio na URL, pra não virar botão confuso
  // pra quem só quer usar o rádio.
  const logAudioDisponivel = useMemo(
    () => new URLSearchParams(window.location.search).has('logaudio'),
    [],
  )
  const [mostrarLogAudio, setMostrarLogAudio] = useState(false)
  const [logAudioTexto, setLogAudioTexto] = useState('')
  useEffect(() => {
    if (!mostrarLogAudio) return
    const t = setInterval(() => setLogAudioTexto(getAudioDebugLog().join('\n')), 500)
    setLogAudioTexto(getAudioDebugLog().join('\n'))
    return () => clearInterval(t)
  }, [mostrarLogAudio])
  const [presences, setPresences] = useState<DialPresence[]>([])
  // Recolhíveis da tela Rádio, com a escolha da pessoa lembrada entre visitas
  // — "No canal" começa aberto (não muda o que já se via), o mapa começa
  // fechado (como sempre foi); os dois passam a persistir depois de mexidos.
  const [showMap, setShowMap] = useState(() => lerPref('radioamador:mapa', false))
  const [mostrarCanal, setMostrarCanal] = useState(() => lerPref('radioamador:no-canal', true))
  // "Áudio limpo": desliga o chiado de banda, só a voz. Booleano de verdade,
  // então lerPref/gravarPref servem — ao contrário de filtro/squelch, que
  // têm mais de dois estados e usam localStorage direto.
  const [ruidoLigado, setRuidoLigado] = useState(() => lerPref('radioamador:ruido', true))
  const [avisoLogin, setAvisoLogin] = useState<string | null>(null)

  // Largura de filtro de FI: preferência do operador, não reseta a cada
  // sessão. Não é booleano — lerPref/gravarPref não servem aqui — então usa
  // localStorage direto, mesmo padrão de Admin.tsx/InstalarApp.tsx.
  const [filterHz, setFilterHz] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem('radioamador:filtro'))
      return v > 0 ? v : FILTRO_HZ[1]
    } catch {
      return FILTRO_HZ[1]
    }
  })
  // Squelch: mesmo padrão do filtro — localStorage direto, porque não é
  // booleano ('desligado' é um VALOR do controle, não a ausência de um). O
  // padrão é "Médio" (ligado): é como um rádio de verdade opera — silêncio
  // com o canal livre, não chiado constante.
  const [squelchDb, setSquelchDb] = useState<number | null>(() => {
    try {
      const v = localStorage.getItem('radioamador:squelch')
      if (v === null) return SQUELCH_DB[2]
      if (v === 'off') return null
      const n = Number(v)
      return Number.isFinite(n) ? n : SQUELCH_DB[2]
    } catch {
      return SQUELCH_DB[2]
    }
  })
  const [scanning, setScanning] = useState(false)
  const [avisoVarredura, setAvisoVarredura] = useState<string | null>(null)
  const [repeaters, setRepeaters] = useState<DialRepeater[]>([])

  const state = useSyncExternalStore(rfStore.subscribe, () => rfStore.snapshot())

  // Reler a identidade da sessão. Ela muda por FORA da tela — aprovação na
  // prova emite indicativo, e a mesa de análise pode adotar um oficial — então
  // o app precisa saber recarregá-la sem exigir que a pessoa saia e volte.
  const recarregarIdentidade = useCallback(() => {
    fetch('/api/auth/me')
      .then((r) => r.json())
      .then((d: { user: User | null }) => setUser(d.user))
      .catch(() => setUser(null))
  }, [])

  // O link mágico chega do cliente de e-mail como /entrar?token=XXX, e é ESTA
  // função que o transforma em sessão.
  //
  // Faltava. O app abria, ignorava o token, perguntava /api/auth/me, recebia
  // null e mostrava o login de novo — a pessoa pedia outro link e caía no
  // mesmo lugar, para sempre. Passou despercebido porque todo teste de
  // navegador usava o botão do modo de desenvolvimento, que chama /verify
  // direto; o único caminho que uma pessoa de verdade percorre era o único
  // que nada exercitava.
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token')
    if (!token) {
      recarregarIdentidade()
      return
    }
    fetch(`/api/auth/verify?token=${encodeURIComponent(token)}`)
      .then(async (r) => ({ ok: r.ok, d: (await r.json()) as { user?: User; message?: string } }))
      .then(({ ok, d }) => {
        // O token sai da URL em qualquer desfecho. Ele é de uso único, mas
        // deixá-lo na barra de endereços o espalha por histórico, referer e
        // captura de tela — e um recarregar da página traria "link já usado".
        window.history.replaceState({}, '', window.location.pathname === '/entrar' ? '/' : window.location.pathname)
        if (ok && d.user) {
          setUser(d.user)
          return
        }
        // Sem isto a pessoa volta ao login sem explicação nenhuma, que é
        // exatamente o laço em que ela fica presa.
        setAvisoLogin(d.message ?? 'esse link expirou ou já foi usado; peça outro')
        setUser(null)
      })
      .catch(() => {
        setAvisoLogin('não foi possível validar o link; tente pedir outro')
        setUser(null)
      })
  }, [recarregarIdentidade])

  useEffect(() => {
    if (!user) return
    let vivo = true
    fetchBands()
      .then((d) => {
        if (!vivo) return
        setBands(d)
        // Quem chega cai no canal de CHAMADA da Faixa do Cidadão, e não mais
        // em 145.500 FM. A diferença é o que a pessoa encontra no primeiro
        // segundo: em 145.500, um operador sem classe vê um PTT desabilitado e
        // não entende por quê; na PX ele aperta e fala.
        //
        // A frequência vem do servidor porque a canalização é dado, não código.
        if (d.start) {
          rfStore.setFreq(d.start.freq_khz)
          controlPlane.tune({ freq_khz: d.start.freq_khz, mode: d.start.mode })
        }
      })
      .catch((e: unknown) => console.error(e))

    // Indicativo e classe NÃO vão na URL: o servidor os pega da sessão.
    controlPlane.connect({ lat: -23.55, lon: -46.63 })
    return () => {
      vivo = false
      controlPlane.disconnect()
    }
  }, [user])

  // Bipes de PTT. Ficam no perfil de estação porque saem NO AR, misturados à
  // voz — são equipamento, não preferência de interface. Recarrega junto com o
  // resto da estação: trocar o roger tem que valer no próximo PTT, não na
  // próxima sessão.
  useEffect(() => {
    if (!user) return
    fetch('/api/station')
      .then((r) => r.json())
      .then((d: { station?: { roger_id?: string; roger_inicio_id?: string }; rogers?: RogerTone[] }) => {
        const acha = (id?: string) => (d.rogers ?? []).find((t) => t.id === id) ?? null
        controlPlane.setTones(acha(d.station?.roger_inicio_id), acha(d.station?.roger_id))
      })
      .catch(() => {})
  }, [user, logVersion])

  // Pontos de atividade na fita de dial e na aba Gente.
  useEffect(() => {
    if (!user) return
    const puxar = () => {
      fetch('/api/activity')
        .then((r) => r.json())
        .then((d: { stations: DialPresence[] | null }) => setPresences(d.stations ?? []))
        .catch(() => {})
    }
    puxar()
    const t = setInterval(puxar, 10_000)
    return () => clearInterval(t)
  }, [user])

  // Repetidoras próximas, uma vez por sessão — para marcar na fita de
  // sintonia (docs/03 §3.4: é por onde a maior parte do QSO local acontece).
  // O endpoint já existe e já avalia acesso; MapView consome o mesmo dado por
  // /api/map.
  useEffect(() => {
    if (!user) return
    fetch(`/api/repeaters?lat=-23.55&lon=-46.63&height_m=10&radius_km=200`)
      .then((r) => r.json())
      .then((d: { repeaters?: DialRepeater[] }) => setRepeaters(d.repeaters ?? []))
      .catch(() => {})
  }, [user])

  const bands = bandsResp?.bands ?? []
  const banda = useMemo(
    () => bands.find((b) => state.freqKHz >= b.low_khz && state.freqKHz <= b.high_khz) ?? null,
    [bands, state.freqKHz],
  )
  // A varredura roda num setInterval criado uma vez; sem ref, o passo leria
  // sempre a banda do instante em que a varredura começou.
  const bandaRef = useRef(banda)
  bandaRef.current = banda

  // Segmentos com o privilégio DESTE operador já resolvido, para a fita pintar.
  const segmentos = useMemo(
    () =>
      (banda?.segments ?? []).map((s) => ({
        low_khz: s.low_khz,
        high_khz: s.high_khz,
        allowed: segmentAllows(s, user?.class ?? '', banda?.open_to_all ?? false),
        // exactOptionalPropertyTypes: a chave não pode existir valendo
        // undefined — só faixa com canal a carrega.
        ...(s.channel ? { channel: s.channel } : {}),
      })),
    [banda, user?.class],
  )

  // Arrastar o dial dispara dezenas de eventos por segundo. A frequência local
  // acompanha na hora; o servidor recebe no máximo a cada 120 ms e ao soltar.
  const ultimoEnvio = useRef(0)
  const pendente = useRef<number | null>(null)

  // Filtro em ref: sintonizar() precisa do valor mais recente sem entrar nas
  // dependências do useCallback — mesma razão de onTuneRef em DialStrip.tsx,
  // isolar o caminho quente de sintonia de recriação por causa de um estado
  // que muda raramente.
  const filterHzRef = useRef(filterHz)
  filterHzRef.current = filterHz

  // Timer da varredura. Declarado aqui (antes de sintonizar) porque
  // sintonizar precisa cancelar uma varredura em curso quando a sintonia vem
  // de fora dela — tocar o dial, mudar de banda — sem confundir isso com o
  // próprio passo da varredura, que também chama sintonizar.
  const scanTimer = useRef<number | null>(null)

  const sintonizar = useCallback(
    (khz: number, modo?: string, imediato = false, viaVarredura = false) => {
      // Sintonia que não veio da própria varredura cancela ela — é a pessoa
      // dizendo "deixa que eu escolho".
      if (!viaVarredura && scanTimer.current !== null) {
        clearInterval(scanTimer.current)
        scanTimer.current = null
        setScanning(false)
      }

      const alvo = Math.round(khz * 2) / 2
      rfStore.setFreq(alvo)

      const mode = modo ?? rfStore.snapshot().channel?.mode ?? (alvo > 30000 ? 'FM' : 'SSB')
      const agora = performance.now()
      if (imediato || agora - ultimoEnvio.current > 120) {
        ultimoEnvio.current = agora
        pendente.current = null
        controlPlane.tune({ freq_khz: alvo, mode, filter_hz: filterHzRef.current })
      } else {
        pendente.current = alvo
      }
    },
    [],
  )

  // Rede de segurança do estrangulamento acima: sem isto, a última posição do
  // arrasto poderia nunca chegar ao servidor.
  useEffect(() => {
    const t = setInterval(() => {
      if (pendente.current === null) return
      const alvo = pendente.current
      pendente.current = null
      ultimoEnvio.current = performance.now()
      const mode = rfStore.snapshot().channel?.mode ?? (alvo > 30000 ? 'FM' : 'SSB')
      controlPlane.tune({ freq_khz: alvo, mode, filter_hz: filterHzRef.current })
    }, 200)
    return () => clearInterval(t)
  }, [])

  // Trocar o filtro tem efeito na hora: reformula o timbre local e reenvia a
  // sintonia atual para o servidor recalcular o SNR com a largura nova
  // (internal/channel/hub.go já lê FilterHz do OUVINTE).
  const mudarFiltro = useCallback(
    (hz: number) => {
      filterHzRef.current = hz
      setFilterHz(hz)
      try {
        localStorage.setItem('radioamador:filtro', String(hz))
      } catch {
        // preferência não persiste, mas continua valendo nesta sessão
      }
      controlPlane.setFilterHz(hz)
      sintonizar(rfStore.snapshot().freqKHz, undefined, true)
    },
    [sintonizar],
  )

  // Squelch é decisão só do OUVINTE — ao contrário do filtro, não afeta o SNR
  // que o servidor calcula para os outros, então não precisa resintonizar.
  const mudarSquelch = useCallback((db: number | null) => {
    setSquelchDb(db)
    try {
      localStorage.setItem('radioamador:squelch', db === null ? 'off' : String(db))
    } catch {
      // preferência não persiste, mas continua valendo nesta sessão
    }
    controlPlane.setSquelch(db)
  }, [])

  // Ao contrário do filtro, não precisa resintonizar nem mexer no SNR — é
  // só o chiado local sumindo ou voltando, igual ao squelch.
  const alternarRuido = useCallback((on: boolean) => {
    setRuidoLigado(on)
    gravarPref('radioamador:ruido', on)
    controlPlane.setNoiseEnabled(on)
  }, [])

  const passo = useCallback(
    (d: number) => sintonizar(rfStore.snapshot().freqKHz + d, undefined, true),
    [sintonizar],
  )

  const irParaBanda = useCallback(
    (b: Band) => {
      // PX se entra pelo canal 11, que é o de chamada — é onde se pergunta
      // "tem alguém aí?". Nas demais faixas, o objetivo é o mesmo: pousar
      // onde a fonia de verdade se concentra, não no meio bruto da banda.
      if (b.open_to_all) {
        sintonizar(27085, 'AM', true)
        return
      }
      const modoAlvo = b.low_khz > 30000 ? 'FM' : 'SSB'
      const segs = b.segments ?? []
      // O maior segmento que já permite o modo de fonia da faixa é, na
      // prática, a área reconhecida de fonia — e evita cair no meio de um
      // trecho só CW/digital (ex.: 30m inteira é WARC, sem fonia nenhuma;
      // o meio bruto da banda caía exatamente ali).
      const fonia = segmentoMaisLargo(segs, (s) => s.modes?.includes(modoAlvo) ?? false)
      if (fonia) {
        sintonizar((fonia.low_khz + fonia.high_khz) / 2, modoAlvo, true)
        return
      }
      // Faixa sem fonia nenhuma (WARC): cai no maior segmento que existir,
      // no modo que ele de fato permite — CW é o padrão universal desse
      // tipo de faixa.
      const maior = segmentoMaisLargo(segs, () => true)
      sintonizar(
        maior ? (maior.low_khz + maior.high_khz) / 2 : (b.low_khz + b.high_khz) / 2,
        maior?.modes?.[0] ?? modoAlvo,
        true,
      )
    },
    [sintonizar],
  )

  // Varredura: avança a frequência sozinha dentro da banda atual até achar
  // atividade em rfStore().scope — o mesmo dado que o waterfall já pinta a
  // 10 Hz, sem round-trip por passo. Circular: chegou ao topo, volta ao
  // início; deu a volta inteira sem achar nada, para e avisa.
  const pararVarredura = useCallback(() => {
    if (scanTimer.current !== null) {
      clearInterval(scanTimer.current)
      scanTimer.current = null
    }
    setScanning(false)
  }, [])

  const alternarVarredura = useCallback(() => {
    if (scanTimer.current !== null) {
      pararVarredura()
      return
    }
    const b = bandaRef.current
    if (!b) return

    const modo = b.open_to_all ? 'AM' : b.low_khz > 30000 ? 'FM' : 'SSB'
    const passoKHz = b.open_to_all ? 10 : 5
    const maxPassos = Math.ceil((b.high_khz - b.low_khz) / passoKHz) + 2
    let passos = 0

    setScanning(true)
    setAvisoVarredura(null)

    scanTimer.current = window.setInterval(() => {
      const banda = bandaRef.current
      if (!banda) {
        pararVarredura()
        return
      }
      const atual = rfStore.snapshot().freqKHz

      const sinais = rfStore.snapshot().scope?.signals ?? []
      const achado = sinais.find((s) => Math.abs(s.freq_khz - atual) < passoKHz / 2)
      if (achado) {
        pararVarredura()
        sintonizar(achado.freq_khz, modo, true, true)
        navigator.vibrate?.(40)
        setAvisoVarredura(
          t.app.sinalEncontradoEm.replace('{freq}', (achado.freq_khz / 1000).toFixed(3)),
        )
        return
      }

      passos++
      if (passos > maxPassos) {
        pararVarredura()
        setAvisoVarredura(t.app.varreduraCompleta)
        return
      }

      let prox = atual + passoKHz
      if (prox > banda.high_khz) prox = banda.low_khz
      sintonizar(prox, modo, true, true)
    }, 220)
  }, [pararVarredura, sintonizar, t])

  useEffect(() => {
    if (!avisoVarredura) return
    const t = setTimeout(() => setAvisoVarredura(null), 5000)
    return () => clearTimeout(t)
  }, [avisoVarredura])

  const ligarAudio = async () => {
    try {
      setAudioErr(null)
      await controlPlane.enableAudio()
      // O AudioEngine nasce de novo a cada "Ligar áudio", com os padrões de
      // fábrica — sem isto, um filtro ou squelch escolhido numa sessão
      // anterior ficaria esquecido até a pessoa tocar no controle de novo.
      controlPlane.setFilterHz(filterHzRef.current)
      controlPlane.setSquelch(squelchDb)
      controlPlane.setNoiseEnabled(ruidoLigado)
      // A tela deixa de apagar durante a operação, e o sistema passa a saber
      // que há áudio em curso — é isso que dá alguma vida ao app com outro
      // aplicativo na frente.
      void travarTela()
    } catch (e) {
      setAudioErr(e instanceof Error ? e.message : 'não foi possível acessar o microfone')
    }
  }

  // Liga o áudio sozinho assim que a conexão abre — sem precisar de botão.
  // O AudioContext já foi destravado no clique de entrar/cadastrar
  // (Login.tsx/ControlPlane.unlockAudio), então só falta pedir microfone e
  // negociar WebRTC. Precisa esperar state.connected (o WebSocket ABERTO, não
  // só chamado): a sinalização SDP é descartada, não enfileirada, se mandada
  // com o socket fechado (ControlPlane.send).
  //
  // Dispara em TODA reconexão, não só na primeira: o WebRTC não sobrevive a
  // uma queda do control plane (client.ts derruba a sessão de mídia órfã no
  // onclose), então cada `state.connected` novo é uma engine nova por
  // baixo. `ligarAudio` → `enableAudio` → `AudioEngine.start()` já é
  // idempotente sozinho quando não há nada para refazer (`if (this.pc)
  // return`), então chamar de novo com o áudio já de pé não faz nada.
  useEffect(() => {
    if (!state.connected) return
    void ligarAudio()
  }, [state.connected])

  // Volta do segundo plano: repede a trava de tela e reconecta na hora, em vez
  // de esperar o backoff com o rádio mudo.
  useEffect(() => acompanharVisibilidade(() => controlPlane.reconectarAgora()), [])

  // A notificação de mídia do sistema mostra onde o operador está. Some quando
  // o áudio não está ligado — anunciar sessão de mídia sem áudio seria mentira
  // para o sistema operacional.
  useEffect(() => {
    if (!user || state.mediaState === null) {
      encerrarSessao()
      return
    }
    anunciarSessao({
      callsign: user.callsign,
      banda: banda ? `${banda.name}${state.channel?.band_id?.startsWith('11m ') ? ' · ' + state.channel.band_id.slice(4) : ''}` : 'Radioamador',
      freqKHz: state.freqKHz,
    })
  }, [user, state.mediaState, state.freqKHz, banda, state.channel?.band_id])

  // A repetidora cuja SAÍDA está sob a agulha agora, se houver — mesma
  // tolerância de Directory.ByOutput no servidor (internal/rfcore/repeater).
  const repeaterAtual = useMemo(
    () => repeaters.find((r) => Math.abs(r.output_mhz * 1000 - state.freqKHz) < 2.5) ?? null,
    [repeaters, state.freqKHz],
  )

  // Tom de "atracou" só na TRANSIÇÃO de sintonizar numa repetidora — não a
  // cada quadro em que ela continua sob a agulha — e só com áudio de verdade
  // ligado, porque é o AudioContext que sabe tocar o tom.
  const repeaterAnterior = useRef<string | null>(null)
  useEffect(() => {
    const atual = repeaterAtual?.callsign ?? null
    if (atual && atual !== repeaterAnterior.current && state.mediaState === 'connected') {
      controlPlane.playLockTone(repeaterAtual!.mode !== 'FM')
    }
    repeaterAnterior.current = atual
  }, [repeaterAtual, state.mediaState])

  const alternarCanal = () =>
    setMostrarCanal((v) => {
      gravarPref('radioamador:no-canal', !v)
      return !v
    })

  const alternarMapa = () =>
    setShowMap((v) => {
      gravarPref('radioamador:mapa', !v)
      return !v
    })

  const sair = async () => {
    soltarTela()
    encerrarSessao()
    await fetch('/api/auth/logout', { method: 'POST' })
    controlPlane.disconnect()
    setUser(null)
  }

  // A própria conta acabou de ser excluída — o servidor já derrubou sessão e
  // cookie no mesmo request (handleContaExcluirPropria), então aqui é só o
  // mesmo desligamento local de sair(), sem chamar /api/auth/logout de novo.
  const contaExcluida = () => {
    soltarTela()
    encerrarSessao()
    controlPlane.disconnect()
    setUser(null)
  }

  if (user === undefined) return <main className="app" />
  if (user === null)
    return (
      <main className="app">
        <Login onEntered={setUser} aviso={avisoLogin} />
      </main>
    )

  const operando = aba === 'radio'
  const noCanal = state.channel?.stations ?? []

  return (
    <div className={operando ? 'casca operando' : 'casca'}>
      <header className="barra">
        <strong>{user.callsign}</strong>
        <span className="pill">{user.class ? `${t.app.classe} ${user.class}` : t.app.ouvinte}</span>
        {/* O nível da identidade fica à vista: um apelido não pode aparecer na
            tela com a mesma autoridade de um indicativo comprovado. */}
        {user.kind !== 'oficial' && (
          <span className="pill fraco" title={user.nota_identidade}>
            {user.kind === 'simulado' ? t.app.daProva : t.app.apelido}
          </span>
        )}
        <span className="cresce" />
        <LangSelector />
        <span
          className={state.connected ? 'estado ok' : 'estado ruim'}
          role="status"
          aria-label={state.connected ? t.app.conectado : t.app.desconectado}
        />
        <button className="link" onClick={() => void sair()}>
          {t.app.sair}
        </button>
      </header>

      <div className="corpo">
        {aba === 'radio' && (
          <section className="tela-radio">
            {/* O áudio liga sozinho assim que a conexão abre (efeito de
                state.connected, acima) — o gesto que precisava vem do
                próprio clique de entrar/cadastrar. Só aparece texto aqui
                enquanto conecta, ou se der errado. */}
            {state.mediaState !== 'connected' && !audioErr && (
              <p className="audio-estado">
                {t.app.audioEstado}: {state.mediaState ?? t.app.audioIniciando}
              </p>
            )}
            {audioErr && (
              <p className="denial">
                {audioErr}{' '}
                <button className="link" onClick={() => void ligarAudio()}>
                  {t.app.tentarNovamente}
                </button>
              </p>
            )}

            {logAudioDisponivel && (
              <button className="link" onClick={() => setMostrarLogAudio((v) => !v)}>
                {mostrarLogAudio ? 'esconder log de áudio' : 'ver log de áudio'}
              </button>
            )}
            {mostrarLogAudio && (
              <div className="log-audio">
                <pre>{logAudioTexto || '(nada ainda — segure o PTT ou espere alguém transmitir)'}</pre>
                <button
                  className="link"
                  onClick={() => void navigator.clipboard?.writeText(logAudioTexto)}
                >
                  copiar
                </button>
              </div>
            )}

            {avisoVarredura && <p className="aviso">{avisoVarredura}</p>}

            <InstalarApp mediaState={state.mediaState} />

            <div className="scope">
              <Waterfall height={96} onTune={(f) => sintonizar(f, undefined, true)} />
            </div>

            <section className="canal">
              {/* Recolhido não pode esconder "tem gente falando agora": a
                  contagem — e o pill de transmissão — ficam no cabeçalho,
                  visíveis mesmo fechado. */}
              <button className="canal-toggle" onClick={alternarCanal}>
                <span>{t.app.noCanal}</span>
                <span className="canal-contagem">
                  {noCanal.length === 0 ? t.app.ninguem : noCanal.length}
                  {noCanal.some((s) => s.transmitting) && (
                    <span className="pill tx">{t.app.noAr}</span>
                  )}
                </span>
              </button>
              {mostrarCanal &&
                (noCanal.length === 0 ? (
                  <p className="vazio">
                    {t.app.canalVazioPre} <b>{t.app.abaGente}</b> {t.app.canalVazioPos}
                  </p>
                ) : (
                  <ul>
                    {noCanal.map((s) => (
                      <li key={s.station_id}>
                        <strong>{s.callsign}</strong>
                        {s.transmitting && <span className="pill tx">{t.app.noAr}</span>}
                      </li>
                    ))}
                  </ul>
                ))}
            </section>

            <section className="mapsec">
              <button className="largo" onClick={alternarMapa}>
                {showMap ? t.app.ocultarMapa : t.app.mapaDePropagacao}
              </button>
              {showMap && <MapView lat={-23.55} lon={-46.63} freqKHz={state.freqKHz} />}
            </section>
          </section>
        )}

        {aba === 'gente' && (
          <Gente
            meuIndicativo={user.callsign}
            freqAtual={state.freqKHz}
            modoAtual={state.channel?.mode ?? 'FM'}
            onTune={(khz, modo) => {
              sintonizar(khz, modo, true)
              setAba('radio')
            }}
          />
        )}

        {aba === 'estacao' && (
          <>
            <Identidade user={user} onChanged={recarregarIdentidade} onExcluida={contaExcluida} />
            <Station refreshKey={logVersion} onSaved={() => setLogVersion((v) => v + 1)} />
            <Shack refreshKey={logVersion} />
          </>
        )}

        {aba === 'progresso' && (
          <>
            <Logbook
              callsign={user.callsign}
              freqKHz={state.freqKHz}
              mode={state.channel?.mode ?? 'FM'}
              lat={-23.55}
              lon={-46.63}
              onSaved={() => setLogVersion((v) => v + 1)}
              {...(noCanal.find((s) => s.transmitting)?.callsign
                ? { suggested: noCanal.find((s) => s.transmitting)!.callsign }
                : {})}
            />
            <Awards refreshKey={logVersion} />
            <Exam onApproved={recarregarIdentidade} />
          </>
        )}

        {aba === 'admin' && user.admin && <Admin meuID={user.id} />}
      </div>

      {/* O dock só existe na aba de operar. Nas outras, o espaço é do conteúdo. */}
      {operando && (
        <Dock
          band={banda}
          aberta={banda?.open_to_all ?? false}
          bands={bands}
          segments={segmentos}
          presences={presences}
          repeaters={repeaters}
          repeaterAtual={repeaterAtual}
          filterHz={filterHz}
          squelchDb={squelchDb}
          ruidoLigado={ruidoLigado}
          scanning={scanning}
          onTune={sintonizar}
          onStep={passo}
          onBand={irParaBanda}
          onFilterChange={mudarFiltro}
          onSquelchChange={mudarSquelch}
          onRuidoChange={alternarRuido}
          onScanToggle={alternarVarredura}
        />
      )}

      <nav className="abas" aria-label={t.app.secoes}>
        {(user.admin ? [...abas, abaAdmin] : abas).map((a) => (
          <button
            key={a.id}
            className={aba === a.id ? 'sel' : ''}
            aria-current={aba === a.id ? 'page' : undefined}
            onClick={() => setAba(a.id)}
          >
            {a.nome}
          </button>
        ))}
      </nav>
    </div>
  )
}
